// MarketPryce Social (Voltr) shapes on a deal and its Action. Re-exported from '@/types/deal'.

/** `marketing_copy.social` (server: Deals::MarketingCopySchema SOCIAL). */
export interface SocialDealCopy {
  /** Hero title, e.g. "Get paid to create for WELD". */
  headline?: string;
  /** "What you get" tiles. Empty until pay is decided. */
  reward_tiles?: SocialRewardTile[];
  /** "Let’s create": spec rows and filming tips. */
  create_specs?: SocialCreateSpecs;
  /** A tile without a url is a placeholder. */
  example_videos?: { title: string; url?: string; thumbnail_url?: string }[];
}

export interface SocialCreateSpecs {
  rows?: { label: string; value: string }[];
  tips?: string[];
}

export interface SocialRewardTile {
  icon: 'wallet' | 'percent' | (string & {});
  value: string;
  caption: string;
}

/** One video on the upload step (server: Api::VoltrVideoDetails). */
export interface StepVideo {
  id: string;
  name: string;
  /** Bytes, from the upload that made it; absent for videos uploaded before sizes were kept. */
  size_bytes?: number;
  status: { label: string; tone: 'success' | 'pending' | 'warning' | 'danger' | (string & {}) };
  /** What the coach wrote about it. */
  notes: string[];
  /** A coach sent it back and the athlete hasn't replaced or removed it. */
  sent_back?: boolean;
  /** What the athlete can do with it: replace it with a new upload, or remove it. */
  actions?: { kind: 'replace' | 'remove' | (string & {}); label: string }[];
}

/** The upload step's extras on a Social deal (server: Api::VoltrVideoDetails). */
export interface StepVideoExtras {
  videos?: StepVideo[];
  /** Replaced and removed videos, kept with their feedback; no longer counted. */
  earlier_videos?: StepVideo[];
  /** While a video is sent back: the hero's button ("Fix 1 video") and the card it jumps to. */
  attention?: { count: number; label: string; video_id: string };
  /** "1 of 2 approved", when the deal needs more than one. */
  progress_label?: string;
  /** "Add another video", once there are videos and the deal is open. */
  add_label?: string;
}

/** Order → Preparing to ship → Shipped → Delivered (server: Api::VoltrActionDetails#tracker). */
export interface ShippingTracker {
  /** `at`: when a reached stage happened (ISO 8601); null when Voltr hasn't stamped it. Absent on an older server. */
  stages: { label: string; reached: boolean; current: boolean; at?: string | null }[];
  /** Once shipped with a carrier or number: "UPS · 1Z999…" and the carrier's page. */
  /** `verb` + `at` (no carrier or number): the app dates the line in the phone's zone; `title` is the server's UTC fallback. */
  tracking: { title: string; detail: string; url: string | null; verb?: string; at?: string } | null;
  /** The brand's reason after it declined the last order. */
  note: string | null;
}

/** Where the athlete is with a social deal (server: Api::SocialProgress). */
export type SocialDealStage = 'new' | 'under_way' | 'over';

/** The `voltr` block of a Voltr deal's Action (server: Api::VoltrActionDetails). */
export interface VoltrActionDetails {
  stage: SocialDealStage;
  /** "Step 2 of 5" while under way, else null: the server's words, shown as they are. */
  progress_label: string | null;
  /** Voltr took the athlete out of the program mid-deal: why the open steps locked (server: Api::VoltrRemoval). */
  removed?: { title: string; body: string } | null;
  sample: {
    product_name?: string;
    status?: string;
    carrier?: string | null;
    tracking_number?: string | null;
    tracking_url?: string | null;
    /** ISO times Voltr recorded: ordered, the brand's decision, shipped, delivered. */
    requested_at?: string | null;
    decided_at?: string | null;
    shipped_at?: string | null;
    delivered_at?: string | null;
  } | null;
}
